import { PaletteColorOptions, ThemeOptions, createTheme } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    default: PaletteColorOptions;
  }

  interface PaletteOptions {
    default?: PaletteColorOptions;
  }

  interface TypeText {
    white: string;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    default: true;
  }
}

const h6 = {
  fontSize: '16px',
  lineHeight: '25.6px',
  fontWeight: 500,
  letterSpacing: '0.15px',
};

// We need to use px instead of rem throughout our custom theme variables.
// This is because DeskingTheme sets a custom `htmlFontSize` value to render
// correctly in chairman, and MUI does not seem to scale user provided rem
// values correctly in that situation.
const paletteAndFonts = {
  palette: {
    primary: {
      main: '#1a6664',
      light: '#409a9c',
    },
    secondary: {
      main: '#42409C',
    },
    warning: {
      main: '#fabd07',
    },
    error: {
      main: '#e22e2b',
      light: '#ff5d5d',
    },
    success: { main: '#65B891' },
    divider: 'rgba(0, 0, 0, 0.12)',
    action: {
      // applies to icon buttons
      active: 'rgba(0, 0, 0, 0.87)',
    },
    default: {
      main: 'rgba(0, 0, 0, 0.87)',
    },
    text: {
      white: 'white',
    },
  },
  typography: {
    fontFamily: 'Lato',
    caption: {
      fontSize: '12px',
      letterSpacing: '0.4px',
    },
    h1: {
      fontSize: '48px',
      lineHeight: '56px',
      fontWeight: 400,
    },
    h2: {
      fontSize: '24px',
      lineHeight: '42px',
      fontWeight: 400,
    },
    h3: {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 400,
    },
    h4: {
      fontSize: '20px',
      lineHeight: '32px',
      fontWeight: 500,
      letterSpacing: '0.15px',
      color: 'rgba(0, 0, 0, 0.87)',
    },
    h5: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 600,
      letterSpacing: '0.15px',
    },
    h6,
    body1: {
      fontSize: '14px',
      color: 'rgba(0, 0, 0, 0.6)',
    },
    body2: {
      fontSize: '12px',
      color: 'rgba(0, 0, 0, 0.6)',
    },
    subtitle1: {
      fontSize: '14px',
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
} satisfies ThemeOptions;
const baseTheme = createTheme(paletteAndFonts);

export const commanderThemeOptions = {
  ...paletteAndFonts,
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Lato';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'inherit' },
        containedPrimary: {
          '&.Mui-disabled': {
            color: '#00000042',
            background: '#2168691F',
          },
        },
        containedSecondary: {
          '&.Mui-disabled': {
            color: '#00000042',
            background: '#42409C1F',
          },
        },
        sizeSmall: {
          fontSize: '14px',
          letterSpacing: '0.46px',
          height: '30px',
          // our font is too tall to have a height of 30 with the default padding
          // just disable the vertical padding and let the flexbox center everything instead
          padding: '0px 12px',
        },
        sizeMedium: {
          height: '36px',
          fontWeight: 600,
          letterSpacing: '0.4px',
        },
        sizeLarge: {
          height: '42px',
          fontSize: '16px',
          letterSpacing: '0.46px',
        },
        outlinedDefault: {
          borderColor: 'var(--commander-colorBorderOutlined)',
          background: 'white',
        },
        textDefault: {
          '&:hover': {
            background: 'transparent',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& > .MuiFormLabel-root': {
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...h6,
          color: baseTheme.palette.text.primary,
          '&.Mui-focused': {
            color: baseTheme.palette.text.primary,
          },
          fontSize: '14px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          '&.Mui-selected': {
            color: baseTheme.palette.secondary.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: baseTheme.palette.secondary.main,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: 'normal',
          maxWidth: '260px',
          '&.Mui-selected': {
            backgroundColor: 'rgb(66, 64, 156, 0.08)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgb(66, 64, 156, 0.16)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        vertical: {
          margin: '16px 40px',
          backgroundColor: '#000000',
          borderRightWidth: 2,
          opacity: '12%',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardWarning: {
          color: '#644C03',
          background: `linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), ${baseTheme.palette.warning.main};`,
          '.MuiAlert-icon': {
            color: '#644c03',
          },
        },
        standardError: {
          '.MuiAlert-icon': {
            color: baseTheme.palette.error.main,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        caption: {
          textTransform: 'inherit',
          margin: '0',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        '&.Mui-focused': {
          background: '#ffffff',
          border: '1px solid var(--commander-colorSecondaryMain)',
          color: 'var(--commander-colorSecondaryMain)',
          fontWeight: 600,
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
        },
        fontSize: '16px',
        border: '1px solid var(--commander-colorBorderOutlined)',
        '&:hover': {
          background: '#ffffff',
          border: '1px solid var(--commander-colorSecondaryMain)',
        },
        '& fieldset': {
          border: 'none',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: '1px solid var(--commander-colorTextPrimary)',
          color: 'var(--commander-colorTextPrimary)',
          textTransform: 'none',
          paddingLeft: '15px',
          paddingRight: '15px',
          '&.Mui-selected': {
            backgroundColor: 'var(--commander-colorTextPrimary)',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: 'var(--commander-colorTextPrimary)',
              color: '#FFFFFF',
            },
          },
        },
        sizeLarge: { padding: '8px 22px' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          // We want dialogs to be roughly 1/3 from the top of the screen. MUIs `DialogContainer`
          // is the size of the viewport and uses a flexbox to position the actual dialog inside
          // of it. We can roughly achieve the desired layout by using pseudo elements to create
          // spacers around the dialog that will naturally shrink as the dialog content grows.
          flexDirection: 'column',
          '&::before': {
            content: '""',
            flex: 1,
          },
          '&::after': {
            content: '""',
            flex: 2,
          },
        },
        paper: {
          backgroundColor: '#FAFAFA',
          '& .MuiInputBase-root': {
            backgroundColor: 'var(--commander-colorPaperBackground)',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 32px',
        },
        dividers: {
          borderTop: 'none',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        variant: 'h4',
      },
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        outlinedSuccess: {
          backgroundColor: `${baseTheme.palette.success.main}14`,
          borderColor: baseTheme.palette.success.main,
        },
        outlinedWarning: {
          backgroundColor: `${baseTheme.palette.warning.main}14`,
          borderColor: baseTheme.palette.warning.main,
        },
        outlinedPrimary: {
          backgroundColor: `${baseTheme.palette.primary.main}14`,
          borderColor: baseTheme.palette.primary.main,
        },
        outlinedSecondary: {
          backgroundColor: `${baseTheme.palette.secondary.main}1F`,
          borderColor: `${baseTheme.palette.secondary.main}B2`,
        },
        sizeSmall: {
          fontSize: '12px',
        },
        label: {
          color: baseTheme.palette.text.primary,
        },
        deleteIcon: {
          color: baseTheme.palette.text.disabled,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within':
            {
              outline: 'none',
            },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '16px',
          '&::-webkit-search-cancel-button': {
            WebkitAppearance: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '.MuiInputBase-root': {
            background: 'white',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: baseTheme.palette.text.primary,
          color: 'white',
        },
        arrow: {
          color: baseTheme.palette.text.primary,
        },
      },
    },
  },
} as ThemeOptions;

const CommanderTheme = createTheme(commanderThemeOptions);

export default CommanderTheme;
